import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import AddModeratorIcon from '@mui/icons-material/AddModerator';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LockIcon from '@mui/icons-material/Lock';
import TuneIcon from '@mui/icons-material/Tune';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  RolePermissionRow,
  ShopRole,
  useDeleteRoleMutation,
  useGetPermissionGridQuery,
  useSavePermissionGridMutation,
  useSaveRoleMutation,
} from '../../api/auth/AuthApi';
import { Role, Screen } from '../../utils/auth';
import { useSnackbar } from '../../utils/snackbar';

const screenLabel: Record<string, string> = {
  BILLING: 'Billing',
  BILLS: 'Bills',
  PURCHASE: 'Purchase Entry',
  OUTSTANDING: 'Customer Dues',
  REPORTS: 'Reports & Dashboard',
  MASTERS: 'Masters & Products',
  STOCK: 'Stock lookup',
  SETTINGS: 'Settings',
  USERS: 'Users & Roles',
  SYSTEM: 'Import Stock',
};

const actions: { key: keyof RolePermissionRow; label: string }[] = [
  { key: 'canView', label: 'View' },
  { key: 'canCreate', label: 'Add' },
  { key: 'canEdit', label: 'Edit' },
  { key: 'canDelete', label: 'Delete' },
];

const headCell = {
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  px: 1.5,
  py: 1,
};

const keyOf = (role: string, screen: string) => `${role}|${screen}`;

/**
 * Roles, in two screens rather than one.
 *
 * <p>The list answers "what roles do we have, who is on them, and what can they
 * open" at a glance. Permissions are a separate step, for one role at a time, so
 * the grid is about that role and nothing else - the earlier version made you read
 * a paragraph before you could find the role you wanted.
 */
const RolePermissions = () => {
  const { openSnackbar } = useSnackbar();
  const { data } = useGetPermissionGridQuery();
  const [savePermissionGrid, { isLoading: saving }] = useSavePermissionGridMutation();
  const [saveRole, { isLoading: creatingRole }] = useSaveRoleMutation();
  const [deleteRole, { isLoading: removingRole }] = useDeleteRoleMutation();

  const [rows, setRows] = useState<Record<string, RolePermissionRow>>({});
  /** Empty means the list; a role code means that role's permission grid. */
  const [role, setRole] = useState<Role>('');
  const [creating, setCreating] = useState(false);
  const [newRole, setNewRole] = useState<ShopRole>({ roleName: '', description: '' });
  const [removing, setRemoving] = useState<ShopRole | null>(null);
  const dirty = useRef(false);

  const roleDetails = useMemo(() => data?.roleDetails ?? [], [data]);
  const screens = useMemo(() => data?.screens ?? [], [data]);

  useEffect(() => {
    if (!data) return;
    // A refetch (window focus, reconnect) must not wipe ticks the owner has not
    // saved yet.
    if (dirty.current) return;
    const next: Record<string, RolePermissionRow> = {};
    data.screens.forEach((screen) => {
      data.roles.forEach((each) => {
        const existing = data.permissions.find((row) => row.role === each && row.screen === screen);
        next[keyOf(each, screen)] = existing ?? { role: each, screen, canView: false, canCreate: false, canEdit: false, canDelete: false };
      });
    });
    setRows(next);
  }, [data]);

  // An owner who cannot manage users could never put it back.
  const locked = (screen: string) => role === 'OWNER' && screen === 'USERS';

  /** How many screens a role can open - the one number that sums up a role. */
  const screenCount = (roleCode?: string) =>
    screens.filter((screen) => rows[keyOf(roleCode ?? '', screen)]?.canView).length;

  /** Keeps a row honest: no Add without View, and losing View loses the rest. */
  const applied = (row: RolePermissionRow, action: keyof RolePermissionRow, value: boolean): RolePermissionRow => {
    const next = { ...row, [action]: value } as RolePermissionRow;
    if (action === 'canView' && !value) {
      next.canCreate = false;
      next.canEdit = false;
      next.canDelete = false;
    }
    if (action !== 'canView' && value) {
      next.canView = true;
    }
    return next;
  };

  const toggle = (screen: Screen, action: keyof RolePermissionRow, value: boolean) => {
    dirty.current = true;
    setRows((prev) => ({ ...prev, [keyOf(role, screen)]: applied(prev[keyOf(role, screen)], action, value) }));
  };

  /** The heading box: this action on every screen at once. */
  const toggleColumn = (action: keyof RolePermissionRow, value: boolean) => {
    dirty.current = true;
    setRows((prev) => {
      const next = { ...prev };
      screens.forEach((screen) => {
        if (locked(screen)) return;
        next[keyOf(role, screen)] = applied(next[keyOf(role, screen)], action, value);
      });
      return next;
    });
  };

  const columnState = (action: keyof RolePermissionRow) => {
    const values = screens.map((screen) => !!rows[keyOf(role, screen)]?.[action]);
    const ticked = values.filter(Boolean).length;
    return { all: ticked > 0 && ticked === values.length, some: ticked > 0 && ticked < values.length };
  };

  const save = async () => {
    try {
      await savePermissionGrid(Object.values(rows)).unwrap();
      dirty.current = false;
      openSnackbar('Permissions saved. Staff see the change at their next sign in.', 'success');
    } catch (error: any) {
      openSnackbar(error?.data?.message || 'Could not save the permissions', 'error');
    }
  };

  const createRole = async () => {
    if (!newRole.roleName.trim()) {
      openSnackbar('Give the role a name', 'warning');
      return;
    }
    try {
      const created = await saveRole(newRole).unwrap();
      openSnackbar(`${created.roleName} created. Tick what it may do, then save.`, 'success');
      setCreating(false);
      setNewRole({ roleName: '', description: '' });
      if (created.roleCode) setRole(created.roleCode);
    } catch (error: any) {
      openSnackbar(error?.data?.message || 'Could not create the role', 'error');
    }
  };

  const confirmRemove = async () => {
    if (!removing?.id) return;
    try {
      await deleteRole(removing.id).unwrap();
      openSnackbar(`${removing.roleName} removed`, 'success');
      setRemoving(null);
    } catch (error: any) {
      openSnackbar(error?.data?.message || 'Could not remove the role', 'error');
    }
  };

  const selected = roleDetails.find((each) => each.roleCode === role);

  /* ------------------------------------------------------------ the list */

  if (!role) {
    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Roles
          </Typography>
          <Button variant="contained" size="small" startIcon={<AddModeratorIcon />} onClick={() => setCreating(true)}>
            Create Role
          </Button>
        </Stack>

        <TableContainer component={Card}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Role', 'Description', 'Logins', 'Can open', 'Action'].map((head) => (
                  <TableCell key={head} sx={headCell} align={head === 'Logins' || head === 'Can open' ? 'center' : 'left'}>
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {roleDetails.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    Loading roles...
                  </TableCell>
                </TableRow>
              )}
              {roleDetails.map((each) => (
                <TableRow key={each.roleCode} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight={600}>
                        {each.roleName}
                      </Typography>
                      {each.systemRole && (
                        <Tooltip title="Shipped with the app - its rights can change, the role itself stays">
                          <LockIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>{each.description || '-'}</TableCell>
                  <TableCell align="center">{each.userCount ?? 0}</TableCell>
                  <TableCell align="center">
                    <Chip size="small" variant="outlined" label={`${screenCount(each.roleCode)} of ${screens.length} screens`} />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Set permissions">
                      <IconButton size="small" color="primary" onClick={() => setRole(each.roleCode || '')} aria-label="Permissions">
                        <TuneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!each.systemRole && (
                      <Tooltip title={Number(each.userCount) > 0 ? 'In use by a login' : 'Remove this role'}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={Number(each.userCount) > 0}
                            onClick={() => setRemoving(each)}
                            aria-label="Remove"
                          >
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <CreateRoleDialog
          open={creating}
          value={newRole}
          saving={creatingRole}
          existing={roleDetails}
          onChange={setNewRole}
          onClose={() => setCreating(false)}
          onCreate={createRole}
        />
        <RemoveRoleDialog role={removing} removing={removingRole} onClose={() => setRemoving(null)} onConfirm={confirmRemove} />
      </Box>
    );
  }

  /* ------------------------------------------------ one role's permissions */

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <IconButton
            size="small"
            onClick={() => {
              setRole('');
              dirty.current = false;
            }}
            aria-label="Back to roles"
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {selected?.roleName ?? role}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selected?.description || 'What this role may do on each screen'} &middot; {selected?.userCount ?? 0} login(s)
            </Typography>
          </Box>
        </Stack>
        <LoadingButton variant="contained" size="small" loading={saving} onClick={save}>
          Save Permissions
        </LoadingButton>
      </Stack>

      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCell}>Screen</TableCell>
              {actions.map((action) => {
                const state = columnState(action.key);
                return (
                  <TableCell key={action.label} align="center" sx={headCell}>
                    <Box>{action.label}</Box>
                    <Tooltip title={`${action.label} on every screen`}>
                      <Checkbox
                        size="small"
                        sx={{ p: 0.25, color: 'inherit', '&.Mui-checked': { color: 'inherit' } }}
                        checked={state.all}
                        indeterminate={state.some}
                        onChange={(event) => toggleColumn(action.key, event.target.checked)}
                      />
                    </Tooltip>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {screens.map((screen) => (
              <TableRow key={screen} hover>
                <TableCell>{screenLabel[screen] || screen}</TableCell>
                {actions.map((action) => (
                  <TableCell key={action.label} align="center">
                    <Tooltip title={locked(screen) ? 'An owner must always keep this, or nobody could give it back' : ''}>
                      <span>
                        <Checkbox
                          size="small"
                          disabled={locked(screen)}
                          checked={!!rows[keyOf(role, screen)]?.[action.key]}
                          onChange={(event) => toggle(screen as Screen, action.key, event.target.checked)}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Alert severity="info" sx={{ mt: 2 }}>
        View puts the screen in the menu. Add, Edit and Delete decide which buttons appear on it. Staff see a change at their next sign in.
      </Alert>
    </Box>
  );
};

/** Making a role is two fields; it does not need to live in the main component. */
const CreateRoleDialog = ({
  open,
  value,
  saving,
  existing,
  onChange,
  onClose,
  onCreate,
}: {
  open: boolean;
  value: ShopRole;
  saving: boolean;
  /** The roles already on the books, so a repeat is caught before the server is asked. */
  existing: ShopRole[];
  onChange: (role: ShopRole) => void;
  onClose: () => void;
  onCreate: () => void;
}) => {
  /*
   * Compared the way the server compares - case and spacing ignored - because that is
   * what decides whether it will be refused. Doing it here as well means the shop is
   * told on the field it is typing in, rather than by a message that appears after it
   * presses Create and then disappears.
   */
  const tidy = (name?: string) => (name ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  const typed = tidy(value.roleName);
  const alreadyUsed = typed.length > 0 && existing.some((role) => tidy(role.roleName) === typed);

  return (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ pb: 1 }}>
      <Typography fontWeight={700} fontSize="1rem">
        Create Role
      </Typography>
      <Typography variant="caption" color="text.secondary">
        It starts with nothing ticked. Give it its rights next.
      </Typography>
    </DialogTitle>
    <Divider />
    <DialogContent sx={{ pt: 2 }}>
      <Stack gap={2} sx={{ mt: 1 }}>
        <TextField
          label="Role Name"
          size="small"
          autoFocus
          required
          error={alreadyUsed}
          helperText={alreadyUsed ? `There is already a role called ${value.roleName.trim()}` : ' '}
          placeholder="Evening Counter"
          value={value.roleName}
          onChange={(event) => onChange({ ...value, roleName: event.target.value })}
        />
        <TextField
          label="Description"
          size="small"
          multiline
          rows={2}
          placeholder="What this role is for"
          value={value.description || ''}
          onChange={(event) => onChange({ ...value, description: event.target.value })}
        />
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
      <Button variant="outlined" color="inherit" size="small" onClick={onClose}>
        Cancel
      </Button>
      <LoadingButton
        variant="contained"
        size="small"
        loading={saving}
        disabled={alreadyUsed || !value.roleName.trim()}
        onClick={onCreate}
      >
        Create
      </LoadingButton>
    </DialogActions>
    </Dialog>
  );
};

/** Says what will happen, in one line, and nothing else. */
const RemoveRoleDialog = ({
  role,
  removing,
  onClose,
  onConfirm,
}: {
  role: ShopRole | null;
  removing: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) => (
  <Dialog open={!!role} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ pb: 1 }}>
      <Typography fontWeight={700} fontSize="1rem">
        Remove {role?.roleName}?
      </Typography>
    </DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="text.secondary">
        The role and its permissions are removed. Nobody is signed out, and no bills or stock are affected.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
      <Button variant="outlined" color="inherit" size="small" onClick={onClose}>
        No, Keep It
      </Button>
      <LoadingButton variant="contained" color="error" size="small" loading={removing} onClick={onConfirm}>
        Yes, Remove
      </LoadingButton>
    </DialogActions>
  </Dialog>
);

export default RolePermissions;
